/* eslint-disable max-len, object-curly-newline, react/jsx-one-expression-per-line, newline-per-chained-call, react/jsx-wrap-multilines, implicit-arrow-linebreak, function-paren-newline, operator-linebreak, react/jsx-curly-newline, react/prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Autorenew from '@mui/icons-material/Autorenew';
import Close from '@mui/icons-material/Close';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import DeleteOutline from '@mui/icons-material/DeleteOutline';

/*
 * Shared "Deny chart" modal for Chart Abstraction (admin monitoring + QA queue).
 * A denial requires at least one logged error (Error Documentation): each error
 * is a category + severity (+ optional note). The score preview mirrors the
 * server: 100 minus the summed severity deductions, floored at 0. Accepted
 * charts always score 100, so scoring only ever appears here.
 */

const SEVERITIES = [
  { value: 'minor', label: 'Minor' },
  { value: 'major', label: 'Major' },
  { value: 'critical', label: 'Critical' },
];

const blankError = () => ({ categoryId: '', severity: 'major', note: '' });

const scoreColor = (score) => {
  if (score >= 90) return 'var(--green-1, #256D23)';
  if (score >= 70) return '#B3650F';
  return 'var(--red-2, #CC4525)';
};

function DenyChartModal({ open, target, categories, severityPoints, acting, onCancel, onConfirm }) {
  const [errors, setErrors] = useState([blankError()]);
  const [reason, setReason] = useState('');

  // Reset the form whenever a new chart is opened for denial.
  useEffect(() => {
    if (open) {
      setErrors([blankError()]);
      setReason('');
    }
  }, [open, target?.id]);

  const points = severityPoints || { minor: 5, major: 15, critical: 30 };

  const validErrors = errors.filter((e) => e.categoryId && e.severity);
  const totalDeduction = validErrors.reduce((sum, e) => sum + (Number(points[e.severity]) || 0), 0);
  const score = Math.max(0, 100 - totalDeduction);
  const canSubmit = validErrors.length > 0 && !acting;

  const catList = useMemo(() => categories || [], [categories]);
  const noCategories = catList.length === 0;

  const updateError = (idx, key, value) =>
    setErrors((prev) => prev.map((e, i) => (i === idx ? { ...e, [key]: value } : e)));
  const addError = () => setErrors((prev) => [...prev, blankError()]);
  const removeError = (idx) =>
    setErrors((prev) => (prev.length === 1 ? [blankError()] : prev.filter((_, i) => i !== idx)));

  const submit = () => {
    if (!canSubmit) return;
    onConfirm({
      reason: reason.trim(),
      errors: validErrors.map((e) => ({
        categoryId: Number(e.categoryId),
        severity: e.severity,
        note: (e.note || '').trim() || undefined,
      })),
    });
  };

  const sevLabel = (value) => {
    const s = SEVERITIES.find((x) => x.value === value);
    const pts = Number(points[value]) || 0;
    return `${s ? s.label : value} · -${pts}`;
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <div style={{ padding: '20px 24px', fontFamily: 'Inter, sans-serif' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <h5 style={{ fontSize: 18, fontWeight: 600, color: 'var(--dark-1, #0E1E40)', margin: 0 }}>Deny this chart</h5>
          <IconButton size="small" onClick={onCancel}><Close style={{ fontSize: 20 }} /></IconButton>
        </div>

        {target && (
          <div style={{ display: 'flex', gap: 8, padding: '10px 12px', borderRadius: 8, marginBottom: 16, background: '#FDF1E3', border: '1px solid #f2d9b3' }}>
            <Autorenew style={{ fontSize: 18, color: '#B3650F', flexShrink: 0 }} />
            <p style={{ fontSize: 13.5, color: '#8a5a12', margin: 0 }}>
              {'Denying '}
              <strong>{target.recordId}</strong>
              {target.repName ? <>{' will send it back to '}<strong>{target.repName}</strong></> : ' will send it back to the abstraction rep'}
              {' to review again. Log the errors found for training and reporting.'}
            </p>
          </div>
        )}

        {noCategories ? (
          <div style={{ padding: '12px 14px', borderRadius: 8, background: 'var(--dark-9, #FBFBFC)', border: '1px solid var(--dark-6, #E0E1E6)', color: 'var(--dark-3, #546077)', fontSize: 13.5, marginBottom: 12 }}>
            No error categories have been set up yet. A workspace admin can add them under Settings → Chart Abstraction before charts can be denied.
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--dark-2, #293855)' }}>Errors found</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: scoreColor(score) }}>{`Score ${score}%`}</span>
            </div>

            {errors.map((e, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}>
                <TextField
                  select
                  size="small"
                  label="Category"
                  value={e.categoryId}
                  onChange={(ev) => updateError(idx, 'categoryId', ev.target.value)}
                  sx={{ flex: 1.4, minWidth: 0 }}
                >
                  {catList.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Severity"
                  value={e.severity}
                  onChange={(ev) => updateError(idx, 'severity', ev.target.value)}
                  sx={{ width: 128, flexShrink: 0 }}
                >
                  {SEVERITIES.map((s) => <MenuItem key={s.value} value={s.value}>{sevLabel(s.value)}</MenuItem>)}
                </TextField>
                <IconButton size="small" onClick={() => removeError(idx)} sx={{ mt: 0.5 }} aria-label="Remove error">
                  <DeleteOutline style={{ fontSize: 19, color: 'var(--dark-4, #7D8698)' }} />
                </IconButton>
              </div>
            ))}

            {errors.some((e) => e.categoryId) && (
              <div style={{ marginBottom: 10 }}>
                {errors.map((e, idx) => (e.categoryId ? (
                  // eslint-disable-next-line react/no-array-index-key
                  <TextField key={`note-${idx}`} size="small" fullWidth label={`Note for error ${idx + 1} (optional)`} value={e.note} onChange={(ev) => updateError(idx, 'note', ev.target.value)} sx={{ mb: 1 }} />
                ) : null))}
              </div>
            )}

            <Button size="small" startIcon={<AddCircleOutline />} onClick={addError} sx={{ textTransform: 'none', color: 'var(--primary, #213D69)', mb: 12 / 8 }}>Add another error</Button>

            <TextField label="Additional notes (optional)" multiline minRows={2} fullWidth value={reason} onChange={(ev) => setReason(ev.target.value)} placeholder="e.g. Diagnosis codes need re-checking against the source document" sx={{ mt: 1 }} />
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
          <Button variant="outlined" onClick={onCancel} sx={{ textTransform: 'none', color: 'var(--dark-2, #293855)', borderColor: 'var(--dark-6, #E0E1E6)' }}>Cancel</Button>
          <Button variant="contained" disabled={!canSubmit || noCategories} onClick={submit} sx={{ textTransform: 'none', backgroundColor: 'var(--red-2, #CC4525)', '&:hover': { backgroundColor: '#a8391d' } }}>Deny &amp; send back</Button>
        </div>
      </div>
    </Dialog>
  );
}

export default DenyChartModal;
